import React, { FC, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'src/components/widgets/buttons';
import { Dialog, dialogContext, DialogWindow, useDialogManager } from 'src/components/widgets/dialog/dialog';
import { backofficeUrl, T } from 'src/shared/helpers';
import styled from 'styled-components';

export const SHOPIFY_DUPLICATION_WARNING_LEARN_MORE_URL = 'https://zakeke.zendesk.com/hc/en-us/articles/360013667339-Behaviour-on-the-Add-to-Cart';
export const SHOPIFY_INTEGRATION_SETTINGS_PATH = '/settings/integration-settings/SHOPIFY';
// Anchor on the Shopify "Add to cart behaviour" settings section, so a deep link can land right on it:
// `${SHOPIFY_INTEGRATION_SETTINGS_PATH}#${ADD_TO_CART_BEHAVIOUR_ANCHOR}`.
export const ADD_TO_CART_BEHAVIOUR_ANCHOR = 'add-to-cart-behaviour';
export const SHOPIFY_DUPLICATION_WARNING_DIALOG_DISMISSED_KEY = 'shopifyDuplicationWarningDialogDismissed';
const SHOPIFY_DUPLICATION_WARNING_REGISTRATION_CUTOFF = Date.UTC(2026, 7, 14);

export const getShopifyDuplicationWarningTitle = () => T._('Please read this', 'Admin');

export const getShopifyDuplicationWarningText = () =>
    T._(
        'By default, a new product in your Shopify catalog is created each time a customer personalizes and adds this item to their cart. This allows the customized product preview to be displayed in the cart and enables the application of Zakeke pricing rules that reduce the product price, when applicable. These duplicate products are automatically marked as unlisted and remain invisible to customers. This behavior can be changed, but certain limitations may apply.',
        'Admin'
    );

export const shouldShowShopifyDuplicationWarningForPublish = (registeredOn: string | null | undefined, isFirstPublishedProduct: boolean) => {
    const registeredOnTime = registeredOn ? Date.parse(registeredOn) : Number.NaN;
    const isRegisteredBeforeCutoff = Number.isFinite(registeredOnTime) && registeredOnTime < SHOPIFY_DUPLICATION_WARNING_REGISTRATION_CUTOFF;

    return isRegisteredBeforeCutoff ? isFirstPublishedProduct : true;
};

const WarningDialogWindow = styled(DialogWindow)`
    background-color: #fff1e8;

    button[type='button']:not([class*='Close']) {
        &:last-child {
            background-color: ${(props) => props.theme.app.component.notificationBanner.warning.iconContainerBGColor};
            border-color: ${(props) => props.theme.app.component.notificationBanner.warning.iconContainerBGColor};
            color: #fff;

            &:hover:not([disabled]) {
                background-color: ${(props) => props.theme.app.component.notificationBanner.warning.iconContainerBGColor};
                opacity: 0.9;
            }
        }
    }

    a {
        color: ${(props) => props.theme.app.component.notificationBanner.warning.iconContainerBGColor};
    }
`;

export const ShopifyDuplicationWarningDialog: FC = () => {
    const navigate = useNavigate();
    const { closeDialog } = useDialogManager();
    const { dialogId } = useContext(dialogContext);

    const close = () => closeDialog(dialogId);

    return (
        <Dialog
            size='small'
            windowDecorator={WarningDialogWindow}
            title={getShopifyDuplicationWarningTitle()}
            buttons={[
                {
                    label: T._("Don't show again", 'Admin'),
                    linkButton: true,
                    onClick: () => {
                        localStorage.setItem(SHOPIFY_DUPLICATION_WARNING_DIALOG_DISMISSED_KEY, 'true');
                        close();
                    }
                },
                {
                    label: T._('Change behavior', 'Admin'),
                    outline: true,
                    onClick: () => {
                        close();
                        navigate(backofficeUrl(SHOPIFY_INTEGRATION_SETTINGS_PATH));
                    }
                },
                {
                    label: T._('Got it', 'Admin'),
                    onClick: close
                }
            ]}
        >
            <p>
                {getShopifyDuplicationWarningText()}{' '}
                <Link href={SHOPIFY_DUPLICATION_WARNING_LEARN_MORE_URL} target='_blank' rel='noreferrer'>
                    {T._('Learn more', 'Admin')}
                </Link>
            </p>
        </Dialog>
    );
};
